import './AppAutocomplete.scss';
import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason
} from '@mui/material';
import { KeyboardEvent, SyntheticEvent, useEffect, useState } from 'react';
import AppText from '../AppText/AppText';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import { visuallyHidden } from '@mui/utils';
import { Box } from '@mui/system';

interface Props {
  itemList: string[];
  label: string;
  value: string | null;
  input?: string;
  width: string;
  onChange: (
    event: SyntheticEvent<Element, Event>,
    value?: string | null,
    reason?: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<string> | undefined
  ) => void;
  onInputChange?: (event,val)=>void;
  handleKey?:(e)=>void
}

const AppAutocomplete = ({
  itemList,
  label,
  value,
  input,
  width,
  onChange,
  onInputChange,
  handleKey
}: Props) => {
  const [highlightedValue, setHighlightedValue] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState<String | null>('')

  const normalizeSearchText = (value: string = '') =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[’`´]/g, "'")
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  // clear stale highlight so a stray Enter doesn't restore the previous search text
  useEffect(() => {
    if (!input) {
      setHighlightedValue(null);
    }
  }, [input]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      if (highlightedValue && input) {
      onChange(event, highlightedValue);
    }
  }
  };

  return (
    <>
    <Box aria-live='polite' sx={visuallyHidden}>{announcement}</Box>
    <Autocomplete
      disablePortal
      tabIndex={0}
      forcePopupIcon={false}
      onChange={onChange}
      disableClearable={false}
      slotProps={{
        clearIndicator:{
          style:{display:'inline-flex', visibility:value?'visible':'hidden'}
        }
      }}
      onKeyDown={handleKeyDown}
      onKeyDownCapture={handleKey}
      onHighlightChange={(_, option) => setHighlightedValue(option)}
      options={itemList}
      filterOptions= {(options,params)=>{
        const inputValue= params.inputValue.trim();
        const normalizedInput = normalizeSearchText(params.inputValue);

        const filtered = options.filter((item)=>
          normalizeSearchText(item).includes(normalizedInput)
        )
        const newAnnouncement = filtered.length > 0? `Searching ${inputValue}, ${filtered.length} results found`:'No Options'
        if(announcement!==newAnnouncement){
          setAnnouncement(newAnnouncement)
        }
        return filtered
      }}
      noOptionsText="No Options"
      sx={{width:width}}
      renderInput={(params) => (
        <AppText label={label} width={width} parentField={params} />
      )}
      value={value}
      inputValue={input}
      onInputChange={onInputChange}
      clearIcon={
        <IconButton tabIndex={0} aria-label="Clear selection" size="small">
          <ClearIcon />  
        </IconButton>
      }
      popupIcon={
        <IconButton aria-label="Open dropdown" size="small">
          <ExpandMoreIcon />
        </IconButton>
      }
    />
    </>
  );
};

export default AppAutocomplete;
